import axios from 'axios';
import { ResponseType } from 'axios/index';
import { apiUrl, wpUrl } from '@/env';
import { IUserProfile, IUserProfileUpdate, IUserProfileCreate, ISignup } from './interfaces';
import { IProductResponce, IProductRequest, IFinanceResponce, IFinanceRequest, IFinancialInfoMaterialsResponse, IFundraisingResponce, IFundraising, IFundraisingReport, ReportValidation } from './store/report';
import { Dictionary } from 'vue-router/types/router';
import { IToDo, Report, ReportResult } from './interfaces/report';
import { IBasic, IFinance, IProduct } from './store/setup';
import { Company, CompanyReports } from './store/admin';
import { IAccountInfo } from './store/user';

function authHeaders(token: string) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

function authHeadersForDownloadFile(token: string) {
  let responseType: ResponseType = 'blob';
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: responseType,
  };
}

function authHeadersWithContentType(token: string, content_type: string) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': content_type
    },
  };
}

export const api = {
  // Common API
  async logInGetToken(username: string, password: string) {
    return axios.post(`${apiUrl}/api/v1/login/access-token`, {
      user_email: username,
      user_password: password,
    });
  },
  async getMe(token: string) {
    return axios.get<IUserProfile>(`${apiUrl}/api/v1/users/me`, authHeaders(token));
  },
  async updateMe(token: string, data: IUserProfileUpdate) {
    return axios.put<IUserProfile>(`${apiUrl}/api/v1/users/me`, data, authHeaders(token));
  },
  async getUsers(token: string) {
    return axios.get<IUserProfile[]>(`${apiUrl}/api/v1/users/`, authHeaders(token));
  },
  async updateUser(token: string, userId: number, data: IUserProfileUpdate) {
    return axios.put(`${apiUrl}/api/v1/signup/${userId}`, data, authHeaders(token));
  },
  async getMyCompany(token: string) {
    return axios.get<Company>(`${apiUrl}/api/v1/users/company`, authHeaders(token));
  },
  async signup(data: ISignup) {
    return axios.post(`${apiUrl}/api/v1/signup`, data);
  },
  async createUser(token: string, data: IUserProfileCreate) {
    return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token));
  },
  async passwordRecovery(user_email: string) {
    return axios.post(`${apiUrl}/api/v1/password-recovery/${user_email}`);
  },
  async passwordReset(password: string, token: string) {
    return axios.post(`${apiUrl}/api/v1/reset-password`, {
      new_password: password,
      token,
    });
  },
  async googleSignup(email: string) {
    const data = {
      user_email: email,
    };
    return axios.post(`${apiUrl}/api/v1/google_signup`, data);
  },
  // Setup API
  async getBasic(token: string) {
    return axios.get<IBasic>(`${apiUrl}/api/v1/setup/basic`, authHeaders(token));
  },
  async setupBasic(token: string, data: IBasic) {
    return axios.post(`${apiUrl}/api/v1/setup/basic`, data, authHeaders(token));
  },
  async getProduct(token: string) {
    return axios.get<IProduct>(`${apiUrl}/api/v1/setup/product`, authHeaders(token));
  },
  async setupProduct(token: string, data: IProduct) {
    return axios.post(`${apiUrl}/api/v1/setup/product`, data, authHeaders(token));
  },
  async getFinance(token: string) {
    return axios.get<IFinance>(`${apiUrl}/api/v1/setup/finance`, authHeaders(token));
  },
  async setupFinance(token: string, data: IFinance) {
    return axios.post(`${apiUrl}/api/v1/setup/finance`, data, authHeaders(token));
  },
  async setupComplete(token: string) {
    return axios.post(`${apiUrl}/api/v1/setup/complete`, '', authHeaders(token));
  },
  // User API
  async getAccountInfo(token: string) {
    return axios.get<IAccountInfo>(`${apiUrl}/api/v1/users/account_info`, authHeaders(token));
  },
  async updateAccountInfo(token: string, data: IAccountInfo) {
    return axios.put(`${apiUrl}/api/v1/users/account_info`, data, authHeaders(token));
  },
  // Report API
  async getReports(token: string) {
    return axios.get<Report[]>(`${apiUrl}/api/v1/reports`, authHeaders(token));
  },
  async getTodos(token: string) {
    return axios.get<IToDo[]>(`${apiUrl}/api/v1/report/todos`, authHeaders(token));
  },
  async getWPTargetSlug(slug_name: string) {
    const qs = new URLSearchParams({
      slug: slug_name,
    });
    return axios.get<string>(`${wpUrl}/wp-json/wp/v2/word?${qs}`);
  },
  async getWPcolumns(slug_id: number | null = null) {
    if (slug_id) {
      return axios.get<string>(`${wpUrl}/wp-json/wp/v2/posts?_embed&per_page=3&word=${slug_id}`);
    } else {
      return axios.get<string>(`${wpUrl}/wp-json/wp/v2/posts?_embed&per_page=3`);
    }
  },
  async checkReportValidation(token: string) {
    return axios.get<ReportValidation>(`${apiUrl}/api/v1/report/check`, authHeaders(token));
  },
  async getReportResult(token: string, id: number) {
    return axios.get<ReportResult>(`${apiUrl}/api/v1/report/${id}`, authHeaders(token));
  },
  async getProductReport(token: string) {
    return axios.get<IProductResponce>(`${apiUrl}/api/v1/products/latest`, authHeaders(token));
  },
  async putProductReport(token: string, product_id: number, data: IProductRequest) {
    return axios.put(`${apiUrl}/api/v1/products/${product_id}`, data, authHeaders(token));
  },
  async getFinanceReport(token: string) {
    return axios.get<IFinanceResponce>(`${apiUrl}/api/v1/finance/latest`, authHeaders(token));
  },
  async putFinanceReport(token: string, finance_id: number, data: IFinanceRequest) {
    return axios.put(`${apiUrl}/api/v1/finance/${finance_id}`, data, authHeaders(token));
  },
  async getFinancialInfoMaterialsReport(token: string) {
    return axios.get<IFinancialInfoMaterialsResponse>(`${apiUrl}/api/v1/financial_info_materials`, authHeaders(token));
  },
  async getFundraisingReport(token: string) {
    return axios.get<IFundraisingResponce>(`${apiUrl}/api/v1/fundraising`, authHeaders(token));
  },
  async putFundraisingReport(token: string, data: IFundraisingReport) {
    return axios.put<IFundraisingResponce>(`${apiUrl}/api/v1/fundraising`, data, authHeaders(token));
  },
  async deleteFundraisingReport(token: string, id: number) {
    return axios.delete(`${apiUrl}/api/v1/fundraising/${id}`, authHeaders(token));
  },
  async applyReport(token: string) {
    return axios.post(`${apiUrl}/api/v1/report`, '', authHeaders(token));
  },
  // Material API
  async getProductMaterials(token: string, id: number) {
    return axios.get<File[]>(`${apiUrl}/api/v1/product_materials/${id}`, authHeaders(token));
  },
  async getProductMaterial(token: string, id: number) {
    return axios.get(`${apiUrl}/api/v1/product_materials/${id}`, authHeadersForDownloadFile(token));
  },
  async updateProductMaterials(token: string, product_materials: File[]) {
    let formData = new FormData();
    for (var i = 0; i < product_materials.length; i++) {
      let material = product_materials[i];
      formData.append('materials', material);
    }
    return axios.post(`${apiUrl}/api/v1/product_materials`, formData, authHeadersWithContentType(token, 'multipart/form-data'));
  },
  async deleteProductMaterials(token: string, id: number) {
    return axios.delete(`${apiUrl}/api/v1/product_materials/${id}`, authHeaders(token));
  },
  async getFinanceMaterial(token: string, id: number) {
    return axios.get(`${apiUrl}/api/v1/finance_materials/${id}`, authHeadersForDownloadFile(token));
  },
  async updateFinanceMaterials(token: string, finance_materials: File[]) {
    let formData = new FormData();
    for (var i = 0; i < finance_materials.length; i++) {
      let material = finance_materials[i];
      formData.append('materials', material);
    }
    return axios.post(`${apiUrl}/api/v1/finance_materials`, formData, authHeadersWithContentType(token, 'multipart/form-data'));
  },
  async deleteFinanceMaterials(token: string, id: number) {
    return axios.delete(`${apiUrl}/api/v1/finance_materials/${id}`, authHeaders(token));
  },
  async getFinancialInfoMaterial(token: string, id: number) {
    return axios.get(`${apiUrl}/api/v1/financial_info_materials/${id}`, authHeadersForDownloadFile(token));
  },
  async updateFinancialInfoMaterials(token: string, kind: number, financial_info_materials: File[]) {
    let formData = new FormData();
    for (var i = 0; i < financial_info_materials.length; i++) {
      let material = financial_info_materials[i];
      formData.append('materials', material);
    }
    const qs = new URLSearchParams({ kind: kind.toString() });
    return axios.post(`${apiUrl}/api/v1/financial_info_materials?${qs}`, formData, authHeadersWithContentType(token, 'multipart/form-data'));
  },
  async deleteFinancialInfoMaterials(token: string, kind: number, id: number) {
    const qs = new URLSearchParams({ kind: kind.toString() });
    return axios.delete(`${apiUrl}/api/v1/financial_info_materials/${id}?${qs}`, authHeaders(token));
  },
  // Admin API
  async getAdminReports(token: string) {
    return axios.get<CompanyReports[]>(`${apiUrl}/api/v1/admin/reports`, authHeaders(token));
  },
  async getAdminProductReport(token: string, report_id: number) {
    return axios.get<IProductResponce>(`${apiUrl}/api/v1/admin/products/${report_id}`, authHeaders(token));
  },
  async getAdminFinanceReport(token: string, report_id: number) {
    return axios.get<IFinanceResponce>(`${apiUrl}/api/v1/admin/finances/${report_id}`, authHeaders(token));
  },
  async getAdminFinancialInfoMaterialsReport(token: string, report_id: number) {
    return axios.get<IFinancialInfoMaterialsResponse>(`${apiUrl}/api/v1/admin/financial_info_materials/${report_id}`, authHeaders(token));
  },
  async getAdminFundraisingReport(token: string, report_id: number) {
    return axios.get<IFundraisingResponce>(`${apiUrl}/api/v1/admin/fundraisings/${report_id}`, authHeaders(token));
  },
  async completeReport(token: string, report_id: number) {
    return axios.put(`${apiUrl}/api/v1/admin/reports/${report_id}/complete`, '', authHeaders(token));
  },
  async reenterRequestReport(token: string, report_id: number, request: string) {
    const payload = {
      request: request
    }
    return axios.put(`${apiUrl}/api/v1/admin/reports/${report_id}/reenter`, payload, authHeaders(token));
  },
  async getAdminAccountInfo(token: string, company_id: number) {
    return axios.get<IAccountInfo>(`${apiUrl}/api/v1/admin/account_info/${company_id}`, authHeaders(token));
  },
};
